<template>
  <Table.Base
    :auto-columns="false"
    class="flex flex-col h-full overflow-hidden"
  >
    <Table.Headers>
      <Table.Header> Your copyright </Table.Header>
      <Table.Header class="w-1/5"> Flagged work </Table.Header>
      <Table.Header
        class="w-[190px] cursor-pointer"
        @click="handleSort('TIMESTAMP')"
      >
        <span class="font-primary">Date</span>
        <Icon
          v-if="isActiveSort('TIMESTAMP')"
          name="ArrowUp"
          :size="12"
          class="stroke-gray-800 ml-2"
          :class="sortIconClasses"
        />
      </Table.Header>
      <Table.Header
        class="w-[190px] cursor-pointer"
        @click="handleSort('SCORE')"
      >
        <span class="font-primary">Infringment match</span>
        <Icon
          v-if="isActiveSort('SCORE')"
          name="ArrowUp"
          :size="12"
          class="stroke-gray-800 ml-2"
          :class="sortIconClasses"
        />
      </Table.Header>
      <Table.Header class="w-[150px]"> Label </Table.Header>
      <Table.Header class="w-[200px]"> </Table.Header>
    </Table.Headers>

    <Table.Row v-if="props.processing" class="relative pointer-events-none">
      <div
        class="border--gradient w-full h-0.5 bg-red-500 absolute -top-px left-0"
      ></div>

      <Table.Cell class="h-[64px]!">
        <div class="text--gradient">Creating report(s)</div>
      </Table.Cell>
    </Table.Row>

    <div class="overflow-auto flex-1" @scroll="emit('scroll', $event)">
      <NoInfringements v-if="props.empty" @create="emit('create')" />

      <template v-else-if="props.loading">
        <Table.Row v-for="n in 5" :key="n">
          <Table.Cell class="h-[64px]!">
            <Skeleton class="min-w-[45px] max-w-[45px] h-[45px] mr-2.5" />
            <Skeleton class="w-[120px] h-[14px]" />
          </Table.Cell>
          <Table.Cell class="h-[64px]!">
            <Skeleton class="min-w-[45px] max-w-[45px] h-[45px] mr-2.5" />
            <Skeleton class="w-[100px] h-[14px]" />
          </Table.Cell>
          <Table.Cell class="h-[64px]!">
            <Skeleton class="w-[90px] h-[14px]" />
          </Table.Cell>
          <Table.Cell class="h-[64px]!">
            <Skeleton class="w-[90px] h-[14px]" />
          </Table.Cell>
          <Table.Cell class="h-[64px]!">
            <Skeleton class="w-[60px] h-[22px]" />
          </Table.Cell>
          <Table.Cell class="h-[64px]!">
            <Skeleton class="w-[80px] h-[32px]" />
          </Table.Cell>
        </Table.Row>
      </template>

      <component
        v-for="infringement in props.infringements"
        :key="infringement.id"
        :is="RouterLink"
        :to="getRowLink(infringement)"
      >
        <Table.Row>
          <Table.Cell class="h-[64px]!">
            <!-- Multi-copyright: stacked thumbnails -->
            <template v-if="infringement.copyrights.length > 1">
              <div
                class="relative mr-2.5 flex items-center justify-center min-w-[45px] max-w-[45px] h-[45px]"
              >
                <ThumbnailIcon
                  v-for="(copyright, i) in infringement.copyrights
                    .slice(0, 3)
                    .reverse()"
                  :key="copyright.id"
                  class="absolute min-w-[36px] max-w-[36px] h-[36px]"
                  :style="
                    getStackedStyles(
                      i,
                      infringement.copyrights.slice(0, 3).length
                    )
                  "
                  :icon-size="36"
                  :type="copyright.type"
                  :image-url="copyright.asset?.previewImageUrl"
                  showShadow
                />
              </div>

              <div class="font-secondary text-[14px] leading-[18px]">
                {{ infringement.copyrights.length }} copyrights
              </div>
            </template>

            <!-- Single copyright -->
            <template v-else>
              <ThumbnailIcon
                class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-[45px] tablet-h:max-w-[45px] tablet-h:h-[45px]"
                :icon-size="45"
                :type="infringement.copyrights[0]?.type"
                :image-url="infringement.copyrights[0]?.asset?.previewImageUrl"
              />

              <div class="w-full">
                <div v-truncate>
                  <div data-truncate-target>
                    {{ infringement.copyrights[0]?.name }}
                  </div>
                </div>
              </div>
            </template>
          </Table.Cell>

          <Table.Cell class="h-[64px]! relative">
            <div
              class="shadow size-7 border border-black/8 flex items-center justify-center rounded-full absolute bg-white left-[-5px]"
            >
              <Icon name="Sort" :size="20" class="rotate-90 stroke-gray-800" />
            </div>

            <ThumbnailIcon
              class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-[45px] tablet-h:max-w-[45px] tablet-h:h-[45px]"
              :icon-size="45"
              :type="infringement.infringingFile.type"
              :image-url="infringement.infringingFile.asset?.previewImageUrl"
            />

            <div class="w-full">
              <div v-truncate>
                <div data-truncate-target>
                  {{ infringement.infringingFile.name }}
                </div>
              </div>
            </div>
          </Table.Cell>

          <Table.Cell class="h-[64px]!">
            {{ formatTimestamp(infringement.timestamp) }}
          </Table.Cell>

          <Table.Cell class="h-[64px]! flex-col items-start">
            <template v-if="infringement.copyrights.length > 1">
              <div
                class="font-secondary text-[14px] leading-[18px] text-gray-400"
              >
                - -
              </div>
            </template>
            <template v-else>
              <div class="flex items-center gap-x-1.5 mb-[3px]">
                <div :class="scoreTextClass(infringement.matches?.level)">
                  {{ infringement.matches?.score }}%
                </div>
                <div class="w-[100px] h-2 rounded-md bg-gray-200 relative">
                  <div
                    class="h-full rounded-md absolute top-0 left-0"
                    :class="scoreBgClass(infringement.matches?.level)"
                    :style="{
                      width: `${getScore(infringement)}%`,
                    }"
                  ></div>
                </div>
              </div>
              <Link @click.prevent="handleMatchesClick(infringement)">
                {{ infringement.matches?.breakdown.length }} matches
              </Link>
            </template>
          </Table.Cell>

          <Table.Cell class="h-[64px]!">
            <Pill
              v-if="infringement.label === InfringementLabel.Report"
              class="flex items-center"
              success
            >
              <Icon
                name="File"
                :size="10"
                class="stroke-green-900 mr-[3.5px]"
              />
              Report
            </Pill>
            <Pill v-else class="flex items-center" warning>
              <Icon
                name="Alert"
                :size="10"
                class="stroke-yellow-700 mr-[3.5px]"
              />
              Flagged
            </Pill>
          </Table.Cell>

          <Table.Cell class="h-[64px]!">
            <template v-if="infringement.copyrights.length > 1">
              <RouterLink :to="getRowLink(infringement)" @click.stop>
                <UiButton>
                  View all {{ infringement.copyrights.length }}
                </UiButton>
              </RouterLink>
            </template>
            <template v-else>
              <RouterLink
                v-if="infringement.label === InfringementLabel.Flag"
                :to="`/infringement/preview/${infringement.id}`"
                @click.stop
              >
                <UiButton>Get report</UiButton>
              </RouterLink>
            </template>
          </Table.Cell>
        </Table.Row>
      </component>
    </div>
  </Table.Base>
</template>

<script setup lang="ts">
import {
  InfringementLabel,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { formatTimestamp } from '@/shared/utils/datetime'
import { Icon, Pill, Skeleton, Table, ThumbnailIcon, UiButton } from '@dacr/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import Link from '@/shared/components/Link.vue'
import NoInfringements from './NoInfringements.vue'
import { Dialogs } from '@/shared/dialogs/types'
import { useDialog } from '@/shared/dialogs/useDialog'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']

type SortField = 'TIMESTAMP' | 'SCORE'
type SortDirection = 'ASC' | 'DESC'

export type InfringementSort = {
  field: SortField
  direction: SortDirection
}

const getStackedStyles = (index: number, total: number) => {
  const rotate = index === 0 ? -8 : index === total - 1 ? 0 : 8
  return { transform: `rotate(${rotate}deg)` }
}

const DEFAULT_DIRECTIONS: Record<SortField, SortDirection> = {
  TIMESTAMP: 'DESC',
  SCORE: 'DESC',
}

const props = defineProps<{
  processing?: boolean
  loading?: boolean
  empty?: boolean
  infringements: InfringementNode[]
  sort: InfringementSort
}>()

const emit = defineEmits<{
  scroll: [event: Event]
  create: []
  sort: [sort: InfringementSort]
}>()

const isActiveSort = (field: SortField) => props.sort.field === field

const sortIconClasses = computed(() => ({
  'rotate-180': props.sort.direction === 'DESC',
}))

const handleSort = (field: SortField) => {
  const direction: SortDirection =
    props.sort.field === field
      ? props.sort.direction === 'ASC'
        ? 'DESC'
        : 'ASC'
      : DEFAULT_DIRECTIONS[field]

  emit('sort', { field, direction })
}

const { open } = useDialog()

const scoreTextClass = (level?: ScoreLevel) => ({
  'text-green-500': level === ScoreLevel.Low,
  'text-yellow-500': level === ScoreLevel.Medium,
  'text-red-500': level === ScoreLevel.High,
})

const getRowLink = (infringement: InfringementNode) => {
  if (infringement.copyrights.length > 1) {
    return `/infringement/group/${infringement.infringingFile.id}/${new Date(infringement.timestamp).getTime()}`
  }

  if (infringement.label === InfringementLabel.Report) {
    return `/infringement/report/${infringement.report?.id}`
  }

  return `/infringement/preview/${infringement.id}`
}

const handleMatchesClick = (infringement: InfringementNode) => {
  open(Dialogs.InfringementMatches, {
    props: {
      matches: infringement.matches,
    },
  })
}

const getScore = (infringement: InfringementNode) => {
  return Math.min(infringement.matches?.score || 0, 100)
}

const scoreBgClass = (level?: ScoreLevel) => ({
  'bg-green-500': level === ScoreLevel.Low,
  'bg-yellow-500': level === ScoreLevel.Medium,
  'bg-red-500': level === ScoreLevel.High,
})
</script>
